// src/modules/app-views/omnichannel/pages/Init/components/CustomerIntelligencePanel.jsx
// *******************************************************************************
// CUSTOMER INTELLIGENCE PANEL
// *******************************************************************************
// Right-side panel for real-time customer intelligence and AI assistance
// Replaces the drawer with a always-visible three-column layout
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect } from 'react';
import { Tabs, Card, Avatar, Tag, Button, Spin, Alert, Space, Typography, List, Input, message, Divider, Statistic, Row, Col } from 'antd';
import { 
  UserOutlined, 
  MessageOutlined, 
  BulbOutlined, 
  RobotOutlined,
  CustomerServiceOutlined,
  HistoryOutlined,
  TagOutlined,
  DollarOutlined,
  StarOutlined,
  ClockCircleOutlined,
  SendOutlined,
  TeamOutlined,
  FileTextOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { Text, Title } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CustomerIntelligencePanel = ({ 
  conversationId,
  customerPhone,
  conversationData,
  isPanelVisible = true
}) => {
  // ---------------------------------------------------------------------------
  // 1. STATE
  // ---------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('overview');
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiChatMessage, setAiChatMessage] = useState('');
  const [aiChatHistory, setAiChatHistory] = useState([]);
  const [refreshInterval, setRefreshInterval] = useState(null);
  
  // ---------------------------------------------------------------------------
  // 2. GLOBAL STATE
  // ---------------------------------------------------------------------------
  const storedSubscription = useZustand('auth', 'subscription');
  
  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  
  // Load customer intelligence when conversation changes
  useEffect(() => {
    if (conversationId && isPanelVisible) {
      loadCustomerIntelligence();
      
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(() => {
        if (conversationId && isPanelVisible) {
          loadCustomerIntelligence();
        }
      }, 30000);
      
      setRefreshInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [conversationId, isPanelVisible]);
  
  // Load AI suggestions when intelligence data changes
  useEffect(() => {
    if (conversationId && intelligenceData && isPanelVisible) {
      loadAiSuggestions();
    }
  }, [conversationId, intelligenceData, isPanelVisible]);
  
  // ---------------------------------------------------------------------------
  // 4. DATA LOADING
  // ---------------------------------------------------------------------------
  
  const loadCustomerIntelligence = async () => {
    if (!conversationId) return;
    
    setLoading(true);
    try {
      // Note: openclawService would be imported if available
      // For now, use mock data
      const mockData = getMockCustomerIntelligence(conversationId);
      setIntelligenceData(mockData);
    } catch (error) {
      console.error('Error loading customer intelligence:', error);
      // Don't show error message for auto-refresh
    } finally {
      setLoading(false);
    }
  };
  
  const loadAiSuggestions = async () => {
    if (!conversationId) return;
    
    setAiLoading(true);
    try {
      // Note: openclawService would be imported if available
      // For now, use mock data
      const mockSuggestions = getMockAiSuggestions();
      setAiSuggestions(mockSuggestions);
    } catch (error) {
      console.error('Error loading AI suggestions:', error);
    } finally {
      setAiLoading(false);
    }
  };
  
  const sendAiChatMessage = async () => {
    if (!aiChatMessage.trim() || !conversationId) return;
    
    const userMessage = aiChatMessage;
    setAiChatMessage('');
    
    // Add user message to chat history
    const newChatHistory = [
      ...aiChatHistory,
      { role: 'user', content: userMessage, timestamp: new Date() }
    ];
    setAiChatHistory(newChatHistory);
    
    try {
      // In a real implementation, this would call an AI chat endpoint
      // For now, simulate a response
      setTimeout(() => {
        const aiResponse = {
          role: 'assistant',
          content: translateX('openclaw_ai_response_simulation', { message: userMessage }),
          timestamp: new Date()
        };
        setAiChatHistory(prev => [...prev, aiResponse]);
      }, 1000);
    } catch (error) {
      console.error('Error sending AI chat message:', error);
      appService.message({ flag: 'e', text: translateX('openclaw_ai_response_failed') });
    }
  };
  
  const handleUseSuggestion = (suggestion) => {
    // This would insert the suggestion into the chat input
    message.info(translateX('openclaw_suggestion_selected'));
    
    // Dispatch event that the Conversation component can listen to
    window.dispatchEvent(new CustomEvent('omnichannel:ai-suggestion-selected', {
      detail: { suggestion }
    }));
  };
  
  const handleQuickAction = (action) => {
    switch (action) {
      case 'create_ticket':
        message.info(translateX('openclaw_creating_ticket'));
        break;
      case 'create_lead':
        message.info(translateX('openclaw_creating_lead'));
        break;
      case 'schedule_followup':
        message.info(translateX('openclaw_scheduling_followup'));
        break;
      default:
        message.info(translateX('openclaw_action_executing', { action }));
    }
  };
  
  // ---------------------------------------------------------------------------
  // 5. RENDER HELPERS
  // ---------------------------------------------------------------------------
  
  const renderOverviewTab = () => {
    if (!intelligenceData?.customer) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Alert
            title={translateX('openclaw_no_conversation_selected')}
            description={translateX('openclaw_no_conversation_selected_description')}
            type="info"
          />
        </div>
      );
    }
    
    const { customer, analysis, conversation } = intelligenceData;
    
    // Helper function to translate sentiment
    const translateSentiment = (sentiment) => {
      switch (sentiment) {
        case 'positive': return translateX('openclaw_positive');
        case 'negative': return translateX('openclaw_negative');
        default: return translateX('openclaw_neutral');
      }
    };
    
    // Helper function to translate urgency
    const translateUrgency = (urgency) => {
      switch (urgency) {
        case 'high': return translateX('openclaw_high');
        case 'medium': return translateX('openclaw_medium');
        default: return translateX('openclaw_low');
      }
    };
    
    // Helper function to translate intent
    const translateIntent = (intent) => {
      switch (intent) {
        case 'purchase_inquiry': return translateX('openclaw_purchase_inquiry');
        case 'support_request': return translateX('openclaw_support_request');
        case 'complaint': return translateX('openclaw_complaint');
        default: return translateX('openclaw_general_inquiry');
      }
    };
    
    return (
      <div style={{ padding: '16px 0' }}>
        {/* Customer Header */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space align="center" style={{ width: '100%', marginBottom: 12 }}>
            <Avatar size={48} icon={<UserOutlined />} />
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ margin: 0 }}>{customer.name}</Title>
              <Text type="secondary" style={{ fontSize: '0.85em' }}>{customer.phone}</Text>
            </div>
          </Space>
          
          {/* Quick Stats */}
          <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
            <Col span={12}>
              <Statistic 
                title={translateX('openclaw_interactions')} 
                value={customer.interactionCount || 0} 
                prefix={<HistoryOutlined />}
                styles={{ content: { fontSize: '1.2em' } }}
              />
            </Col>
            <Col span={12}>
              <Statistic 
                title={translateX('openclaw_satisfaction')} 
                value={customer.satisfactionScore || 'N/A'} 
                prefix={<StarOutlined />}
                styles={{ content: { fontSize: '1.2em' } }}
              />
            </Col>
          </Row>
          
          {/* Customer Tags */}
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color="blue" size="small">{translateX('openclaw_whatsapp_user')}</Tag>
            <Tag color="green" size="small">{translateX('openclaw_existing_customer')}</Tag>
            {customer.tags?.slice(0, 2).map((tag, index) => (
              <Tag key={index} color="default" size="small">{tag}</Tag>
            ))}
            {customer.tags?.length > 3 && (
              <Tag color="default" size="small">+{customer.tags.length - 3}</Tag>
            )}
          </Space>
        </Card>
        
        {/* Conversation Analysis */}
        <Card size="small" title={translateX('openclaw_conversation_analysis')} style={{ marginBottom: 16 }}>
          <Space orientation="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>{translateX('openclaw_sentiment')}: </Text>
              <Tag color={
                analysis?.sentiment === 'positive' ? 'green' :
                analysis?.sentiment === 'negative' ? 'red' : 'default'
              } size="small">
                {translateSentiment(analysis?.sentiment || 'neutral')}
              </Tag>
            </div>
            
            <div>
              <Text strong>{translateX('openclaw_intent')}: </Text>
              <Tag color="purple" size="small">{translateIntent(analysis?.intent || 'unknown')}</Tag>
            </div>
            
            <div>
              <Text strong>{translateX('openclaw_urgency')}: </Text>
              <Tag color={
                analysis?.urgency === 'high' ? 'red' :
                analysis?.urgency === 'medium' ? 'orange' : 'default'
              } size="small">
                {translateUrgency(analysis?.urgency || 'low')}
              </Tag>
            </div>
            
            {conversation && (
              <div>
                <Text strong>{translateX('openclaw_duration')}: </Text>
                <Text type="secondary">
                  {Math.round((new Date() - new Date(conversation.createdAt)) / (1000 * 60))} min
                </Text>
              </div>
            )}
          </Space>
        </Card>
        
        {/* Quick Actions */}
        <Card size="small" title={translateX('openclaw_quick_actions')}>
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Button 
              type="primary" 
              size="small" 
              block
              icon={<CustomerServiceOutlined />}
              onClick={() => handleQuickAction('create_ticket')}
            >
              {translateX('openclaw_create_ticket')}
            </Button>
            
            <Button 
              size="small" 
              block
              icon={<TeamOutlined />}
              onClick={() => handleQuickAction('create_lead')}
            >
              {translateX('openclaw_create_lead')}
            </Button>
            
            <Button 
              size="small" 
              block
              icon={<ClockCircleOutlined />}
              onClick={() => handleQuickAction('schedule_followup')}
            >
              {translateX('openclaw_schedule_followup')}
            </Button>
          </Space>
        </Card>
      </div>
    );
  };
  
  const renderAiAssistantTab = () => {
    return (
      <div style={{ padding: '16px 0' }}>
        {/* AI Response Suggestions */}
        <Card 
          size="small"
          title={translateX('openclaw_ai_suggestions')} 
          extra={<Button type="link" size="small" onClick={loadAiSuggestions} loading={aiLoading}>
            {translateX('openclaw_refresh')}
          </Button>}
          style={{ marginBottom: 16 }}
        >
          {aiLoading ? (
            <Spin size="small" tip={translateX('loading') + '...'} />
          ) : aiSuggestions.length > 0 ? (
            <List
              size="small"
              dataSource={aiSuggestions.slice(0, 3)}
              renderItem={(suggestion, index) => (
                <List.Item style={{ padding: '8px 0' }}>
                  <Card 
                    size="small" 
                    style={{ width: '100%', cursor: 'pointer' }}
                    onClick={() => handleUseSuggestion(suggestion)}
                  >
                    <Text style={{ fontSize: '0.9em' }}>{suggestion}</Text>
                    <div style={{ textAlign: 'right', marginTop: 4 }}>
                      <Button 
                        type="link" 
                        size="small" 
                        icon={<SendOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUseSuggestion(suggestion);
                        }}
                      >
                        {translateX('openclaw_use')}
                      </Button>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <Alert title={translateX('openclaw_no_suggestions_yet')} type="info" size="small" />
          )}
        </Card>
        
        {/* AI Chat Assistant */}
        <Card size="small" title={translateX('openclaw_ai_assistant')}>
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12 }}>
            {aiChatHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <RobotOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                <Text type="secondary">{translateX('openclaw_ask_ai')}</Text>
              </div>
            ) : (
              aiChatHistory.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    backgroundColor: msg.role === 'user' ? '#f0f0f0' : '#e6f7ff',
                    borderRadius: 8,
                    fontSize: '0.85em'
                  }}
                >
                  <Text>{msg.content}</Text>
                </div>
              ))
            )}
          </div>
          
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              placeholder={translateX('openclaw_ask_ai') + '...'}
              value={aiChatMessage}
              onChange={(e) => setAiChatMessage(e.target.value)}
              onPressEnter={sendAiChatMessage}
            />
            <Button 
              type="primary" 
              size="small"
              icon={<SendOutlined />}
              onClick={sendAiChatMessage}
              disabled={!aiChatMessage.trim()}
            >
              {translateX('openclaw_send')}
            </Button>
          </Space.Compact>
        </Card>
      </div>
    );
  };
  
  const renderCrmTab = () => {
    return (
      <div style={{ padding: '16px 0' }}>
        <Card size="small" title={translateX('openclaw_crm_integration')} style={{ marginBottom: 16 }}>
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: '0.85em' }}>
              {translateX('openclaw_connect_conversation_crm')}
            </Text>
            
            <Divider style={{ margin: '12px 0' }} />
            
            <Button 
              type="primary" 
              size="small" 
              block
              icon={<FileTextOutlined />}
              onClick={() => handleQuickAction('create_ticket')}
            >
              {translateX('openclaw_create_support_ticket')}
            </Button>
            
            <Button 
              size="small" 
              block
              icon={<RiseOutlined />}
              onClick={() => handleQuickAction('create_lead')}
            >
              {translateX('openclaw_convert_to_lead')}
            </Button>
            
            <Button 
              size="small" 
              block
              icon={<TeamOutlined />}
              onClick={() => handleQuickAction('assign_to_team')}
            >
              {translateX('openclaw_assign_to_team')}
            </Button>
            
            <Button 
              size="small" 
              block
              icon={<TagOutlined />}
              onClick={() => handleQuickAction('add_tags')}
            >
              {translateX('openclaw_add_tags')}
            </Button>
          </Space>
        </Card>
        
        {intelligenceData?.analysis?.recommendedAction && (
          <Card size="small" title={translateX('openclaw_recommended_action')}>
            <Alert
              title={intelligenceData.analysis.recommendedAction}
              type="info"
              showIcon
              action={
                <Button size="small" type="primary">
                  {translateX('openclaw_execute')}
                </Button>
              }
            />
          </Card>
        )}
      </div>
    );
  };
  
  // ---------------------------------------------------------------------------
  // 6. RENDER
  // ---------------------------------------------------------------------------
  
  if (!isPanelVisible) {
    return (
      <div style={{ padding: 24, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>
          <RobotOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 16 }} />
          <Text type="secondary">{translateX('openclaw_open_conversation_ai_insights')}</Text>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Panel Header */}
      <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0 }}>
            <RobotOutlined /> {translateX('openclaw_customer_intelligence_panel')}
          </Title>
          <Button 
            type="link" 
            size="small" 
            onClick={loadCustomerIntelligence}
            loading={loading}
          >
            {translateX('openclaw_refresh')}
          </Button>
        </Space>
      </div>
      
      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        style={{ flex: 1, minHeight: 0 }}
        tabBarStyle={{ margin: '0 16px', paddingTop: 8 }}
        items={[
          {
            key: 'overview',
            label: <span><BulbOutlined /> {translateX('openclaw_overview')}</span>,
            children: (
              <div style={{ height: '100%', overflowY: 'auto', padding: '0 16px 16px 16px' }}>
                {renderOverviewTab()}
              </div>
            ),
          },
          {
            key: 'ai',
            label: <span><RobotOutlined /> {translateX('openclaw_ai_assistant')}</span>,
            children: (
              <div style={{ height: '100%', overflowY: 'auto', padding: '0 16px 16px 16px' }}>
                {renderAiAssistantTab()}
              </div>
            ),
          },
          {
            key: 'crm',
            label: <span><TeamOutlined /> {translateX('openclaw_crm_actions')}</span>,
            children: (
              <div style={{ height: '100%', overflowY: 'auto', padding: '0 16px 16px 16px' }}>
                {renderCrmTab()}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

// *******************************************************************************
// MOCK DATA FUNCTIONS
// *******************************************************************************

const getMockCustomerIntelligence = (conversationId) => {
  return {
    success: true,
    conversationId,
    customer: {
      name: 'Maria Silva',
      phone: '+5511999999999',
      email: 'maria.silva@example.com',
      companyName: 'Tech Solutions Ltda',
      tags: ['vip', 'enterprise'],
      notes: 'Prefers WhatsApp communication, interested in enterprise plan',
      interactionCount: 8,
      lastInteraction: new Date().toISOString(),
      totalSpent: 12500.00,
      averageTicket: 1562.50,
      favoriteProducts: ['Enterprise Plan', 'Premium Support'],
      openTickets: 1,
      satisfactionScore: 4.7,
    },
    conversation: {
      messageCount: 24,
      avgResponseTime: 2.5,
      status: 'active',
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      updatedAt: new Date().toISOString(),
      assignedTo: 'current-user',
    },
    analysis: {
      sentiment: 'positive',
      intent: 'purchase_inquiry',
      urgency: 'medium',
      summary: 'Customer is interested in upgrading to enterprise plan, asking about pricing and features.',
      recommendedAction: 'transfer_to_sales',
    },
    timestamp: new Date().toISOString(),
  };
};

const getMockAiSuggestions = () => {
  return [
    "I'd be happy to provide you with more information about our enterprise plan. It includes advanced features like...",
    "The enterprise plan starts at R$ 1,500/month and includes priority support, custom integrations, and unlimited users.",
    "Would you like me to schedule a demo with our sales team to show you all the enterprise features?",
  ];
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CustomerIntelligencePanel;